/**
 * 入力画面の席モード（タブレット。Issue #711。索引 #709）の形の道具。判断は持たない（答える番か・見えるかはサーバーの印）。
 *
 * - 両席に同じものが届き、誰に見えるかは盤面のカード・選択肢の `visible_to`（Issue #664）。**隠すのは画面**: この席に見えないカードは
 *   card_id を消して裏側で描く（stage.js は code が無ければ裏を出す）
 * - 答えられるのは問い合わせの player がこの席のときだけ（開始の問い合わせは P1。登録はその項目のプレイヤー）
 * - 初手の登録のあいだは席ごとの問い合わせ（`input.seat_queries`）を今の問い合わせとして出す（両席が同時に登録する。Issue #750）
 */

/** URL の ?seat=1|2 → 席（0 / 1）。無ければ null（席なし＝今までどおり両方を入力） */
export function seatFromParam(v) {
  if (v === '1') return 0;
  if (v === '2') return 1;
  return null;
}

const visibleTo = (x, seat) => !Array.isArray(x?.visible_to) || x.visible_to.includes(seat);

/** 盤面（duel_info 互換）のうち、この席に見えないカードを裏側にする（元は変えない） */
export function maskBoardForSeat(board, seat) {
  if (!board || seat == null) return board;
  const out = { ...board };
  Object.entries(board).forEach(([zone, rows]) => {
    if (!Array.isArray(rows)) return;
    out[zone] = rows.map((cards) => (Array.isArray(cards)
      ? cards.map((c) => (c && typeof c === 'object' && !visibleTo(c, seat) ? { ...c, card_id: null, hidden: true } : c))
      : cards));
  });
  return out;
}

/** 問い合わせの選択肢のうち、この席に見えないカードの中身を消す（相手の問い合わせを見せるとき） */
export function maskQueryForSeat(query, seat) {
  if (!query || seat == null || !Array.isArray(query.options)) return query;
  return { ...query, options: query.options.map((o) => (visibleTo(o, seat) ? o : { ...o, code: null, name: '裏側のカード', label: '裏側のカード' })) };
}

/** この席が今の問い合わせに答えるか */
export function isMyTurn(query, seat) {
  if (seat == null) return true;
  return !!query && Number(query.player ?? 0) === seat;
}

/**
 * この席が答える問い合わせ: 席ごとの問い合わせ `input.seat_queries[席]`（初手の登録のあいだ両席に同時に出る。Issue #750）があればそれ、
 * 無ければ今の問い合わせ `input.query`
 */
export function seatQuery(input, seat) {
  if (!input) return null;
  return (seat != null ? input.seat_queries?.[String(seat)] : null) ?? input.query ?? null;
}

/** 席向けの状態（OcgSeatState の state＋input）を画面に出す形に: 見えないカードを裏に、この席の問い合わせを query に、相手の問い合わせの中身を消す */
export function stateForSeat(state, seat) {
  if (!state || seat == null) return state;
  const q = seatQuery(state.input, seat);
  const input = state.input ? { ...state.input, query: isMyTurn(q, seat) ? q : maskQueryForSeat(q, seat) } : state.input;
  return { ...state, board: maskBoardForSeat(state.board, seat), input };
}

// ---------------------------------------------------------------- iPad と同じところまで（Issue #717）

/** 「さっき通したところ」は席モードでは自分の席の分だけ（席なしは両方） */
export function seatChances(chances, seat) {
  if (seat == null) return chances ?? [];
  return (chances ?? []).filter((c) => Number(c.player ?? 0) === seat);
}

/**
 * 相手が答えるカードの選択で、この席が指せる選択肢（候補のうち自分がコントロールするカード）。決めるのは答える席。
 * 席モードで相手の番・自動で通さない・カードを選ぶ（view が select）問い合わせだけ
 */
export function helperOptions(query, seat) {
  if (seat == null || !query || isMyTurn(query, seat) || query.auto || query.source !== 'respond' || query.view?.type !== 'select') return new Set();
  return new Set((query.options ?? []).filter((o) => (o.zone?.controller ?? o.controller) === seat && !o.special).map((o) => o.index));
}

/** 場所の文字は答える側から見たもの（自分のカードが「相手のモンスター 3」）なので、この席から見た文字に */
export function ownLocationText(text) {
  return typeof text === 'string' && text.startsWith('相手の') ? text.slice(3) : text;
}

/** P2 の席は全体・フィールドの見え方を 180° 回して自分の側を下に（手札の見え方は帯がもともと読める向き） */
export function flipsView(view, seat) {
  return seat === 1 && (view === 'all' || view === 'field');
}

/** サーバーの途中経過 → 今の問い合わせで選んでいる選択肢（足した順）。別の問い合わせのものは捨てる */
export function progressPicks(progress, query) {
  if (!progress || !query || progress.query_id !== query.id) return [];
  return (progress.picks ?? []).map((p) => p.index);
}
